import {
  ArrowDown,
  ArrowUp,
  CornerDownRight,
  FileSpreadsheet,
  MoreHorizontal,
  Plus,
  Redo2,
  Trash2,
  Undo2,
} from "lucide-react";
import {
  ClipboardEvent,
  CompositionEvent,
  CSSProperties,
  Dispatch,
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  MutableRefObject,
  Ref,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

import { EmptyRow } from "@buildpy/ui/empty-state";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@buildpy/ui/dropdown-menu";
import { formatNumber } from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import { formatAmount } from "@/modules/quote/ui/format";
import { HistoryAction } from "@buildpy/core/modules/quote/editor/history";
import { blankFilledItem } from "@buildpy/core/modules/quote/editor/paste";
import { stateAreaM2 } from "@buildpy/core/modules/quote/editor/reducer";
import {
  clearCellsAction,
  commitCell,
  commitMarginText,
  editTextOf,
  isBlankRow,
  selectionStatsOf,
} from "@buildpy/core/modules/quote/editor/sheet/cell-edit";
import {
  cellText,
  fillDownAction,
  firstChangedCell,
  isCellEditable,
  isDataRow,
  itemRefsIn,
  normalizeRange,
  pasteWarningOf,
  planPaste,
  rangeToTsv,
  sheetColumns,
  SheetColumnKey,
  SheetRow,
  sheetRowKey,
  sheetRowsOf,
} from "@buildpy/core/modules/quote/editor/sheet/grid-model";
import {
  CellPos,
  clampCol,
  clearPending,
  commandOf,
  EditMode,
  endComposition,
  fillRangeOf,
  gateKeyDown,
  ImeGate,
  initImeGate,
  insertPositionBelow,
  isLastInTrade,
  KeyInput,
  moveRow,
  nearestDataRow,
  pasteSelection,
  resolveRow,
  SheetCommand,
  startComposition,
  wantsScroll,
} from "@buildpy/core/modules/quote/editor/sheet/keymap";
import { cancelsEditOnLock, commitsOnMove, lockedNow, openUnlessLocked, unlessLockedEdit } from "@/modules/quote/ui/editor/locked-guard";
import { lockedDescribedBy } from "@/modules/quote/ui/editor/locked-banner";
import SaleOverrideNote from "@/modules/quote/ui/editor/sale-override-note";
import { SheetItemRow, SheetTradeRow } from "@/modules/quote/ui/editor/sheet/sheet-rows";
import { SHEET_COMMIT_TOAST, slotToast } from "@/modules/quote/ui/editor/toast-slot";
import { EditorAction, EditorState, ItemRef } from "@buildpy/core/modules/quote/editor/types";
import { VatMode } from "@buildpy/core/modules/money/vat";
import { UNIT_SUGGESTIONS } from "@buildpy/core/modules/template/trades";
import { TemplateItem } from "@buildpy/core/modules/template/types";

interface Props {
  state: EditorState;
  dispatch: Dispatch<HistoryAction>;
  simple: boolean;
  // 원가·마진 열을 보일지 · 마진 칸은 vatMode 기준으로 센다.
  showCost?: boolean;
  vatMode?: VatMode;
  spaces: { code: string; label: string }[];
  templateByTrade: ReadonlyMap<string, TemplateItem[]>;
  onSuggest: (ref: ItemRef) => void;
  canUndo: boolean;
  canRedo: boolean;
  /** 보기를 바꿔 시트가 다시 서도 커서·앵커를 이어 가려고 편집기가 들고 있는 자리. */
  memory?: MutableRefObject<SheetMemory | null>;
  /** 엑셀 가져오기 다이얼로그를 연다. 다이얼로그는 편집기가 그린다. */
  onImport?: () => void;
  /** 표 밖으로 나가며 못 읽는 값을 버렸다(값 글자). */
  onAbandon?: (text: string) => void;
  /** 가져오기 다이얼로그가 닫힌 뒤 포커스를 돌려받을 「엑셀 가져오기」 단추. */
  importButtonRef?: Ref<HTMLButtonElement>;
  /** 밖에서 추가한 줄(빠진 항목 등)로 커서를 옮긴다. seq 가 바뀔 때마다 한 번. */
  jumpTo?: { itemKey: string; seq: number } | null;
  /** jumpTo 를 받아 처리했다 — 편집기가 비워 다음에 시트가 다시 설 때 옛 줄로 튀지 않게 한다. */
  onJumped?: () => void;
  // 잠긴 견적 — 칸 편집을 열지 않는다(단추는 표 fieldset 이 끄고, 공종 접기만 그대로 눌린다).
  disabled?: boolean;
  // 편집기 lockedRef 를 그때 읽는 잠김 — 조합 끝 setTimeout 처럼 재렌더 전에 도는 확정을 막는다.
  isLocked: () => boolean;
}

/** 커서·앵커. 행은 키로 따라가고, 키가 사라지면 index 근처로 떨어진다. */
export type Pos = CellPos;

export interface SheetMemory {
  cursor: Pos;
  anchor: Pos;
  scrollTop?: number;
  scrollLeft?: number;
  /** 보기 단추로 막 넘어왔다 — 다시 설 때 한 번만 표로 포커스를 가져온다. */
  focus?: boolean;
}

export const SHEET_START: Pos = { key: null, index: 0, col: 0 };

// 보기를 바꿔 다시 설 때 떠날 때의 스크롤로 — 커서 칸 보이기를 걸지 않는다(걸면 돌려놓은 자리를 곧바로 덮는다).
export const restoreSheetScroll = (
  container: Pick<HTMLElement, "scrollTop" | "scrollLeft">,
  saved: Pick<SheetMemory, "scrollTop" | "scrollLeft">
): void => {
  container.scrollTop = saved.scrollTop ?? 0;
  container.scrollLeft = saved.scrollLeft ?? 0;
};
const START = SHEET_START;

type Command = SheetCommand;

// 1100px 창(본문 약 840px)에서 상세 9열이 가로 스크롤 없이 들어가는 폭 — 합 828. 남는 폭은 작업내역이 먹는다.
const WIDTH: Record<SheetColumnKey, number> = {
  space_code: 80,
  scope: 88,
  item: 168,
  unit: 44,
  qty: 64,
  material_unit_price: 80,
  labor_unit_price: 80,
  simple_price: 88,
  amount: 92,
  cost_unit_price: 84,
  margin: 60,
  note: 100,
};
const ROWHEAD_WIDTH = 32;
const EMPTY_TEMPLATE: TemplateItem[] = [];
const SUGGEST_FIELDS = new Set(["scope", "item", "unit"]);
// 잠긴 견적에서 받는 키 — 이동·선택·복사·취소뿐.
export const LOCKED_COMMANDS: ReadonlySet<Command["kind"]> = new Set(["hold", "move", "enter", "tab", "cancel", "selectAll", "copy"]);
const UNITS: readonly string[] = [...UNIT_SUGGESTIONS, "식"];

// 칸이 보이는 자리(머리글 아래·행 번호 오른쪽)에 들도록 표 안에서만 최소로 민다 — 창은 안 움직인다.
const revealCell = (container: HTMLElement, rect: DOMRect, box: DOMRect) => {
  const headH = container.querySelector("thead")?.getBoundingClientRect().height ?? 0;
  const top = rect.top - box.top - container.clientTop;
  const left = rect.left - box.left - container.clientLeft;
  if (top < headH) container.scrollTop += top - headH;
  else if (top + rect.height > container.clientHeight) {
    container.scrollTop += top + rect.height - container.clientHeight;
  }
  if (left < ROWHEAD_WIDTH) container.scrollLeft += left - ROWHEAD_WIDTH;
  else if (left + rect.width > container.clientWidth) {
    container.scrollLeft += Math.min(
      left + rect.width - container.clientWidth,
      left - ROWHEAD_WIDTH
    );
  }
};

const rowKeyOf = sheetRowKey;

const samePos = (a: Pos, b: Pos): boolean =>
  a.key === b.key && a.index === b.index && a.col === b.col;

const formatSum = (value: number): string =>
  Number.isInteger(value)
    ? formatAmount(value)
    : formatNumber(value);

/** 엑셀처럼 쓰는 견적 시트. 편집기 상태·dispatch 는 카드 보기와 한 벌이다. */
const SheetView = ({
  state,
  dispatch,
  simple,
  showCost = false,
  vatMode = "INCL",
  spaces,
  templateByTrade,
  onSuggest,
  canUndo,
  canRedo,
  memory,
  onImport,
  onAbandon,
  importButtonRef,
  jumpTo = null,
  onJumped,
  disabled = false,
  isLocked,
}: Props) => {
  const columns = useMemo(() => sheetColumns(simple, showCost), [simple, showCost]);
  // 공종 줄(이름·항목 추가·삭제)에 넘기는 dispatch — 재렌더 전 잠김도 버리고 접기만 보낸다.
  const tradeRowDispatch = useMemo(() => unlessLockedEdit(lockedNow(disabled, isLocked), dispatch), [disabled, isLocked, dispatch]);
  const amountCol = columns.findIndex((c) => c.key === "amount");

  const rows = useMemo(() => {
    const collapsed = new Set(state.trades.filter((t) => t.collapsed).map((t) => t.key));
    return sheetRowsOf(state).filter(
      (row) => row.kind === "trade" || !collapsed.has(row.ref.tradeKey)
    );
  }, [state]);

  const indexByKey = useMemo(
    () => new Map(rows.map((row, index) => [rowKeyOf(row), index])),
    [rows]
  );

  const itemCount = useMemo(
    () => state.trades.reduce((sum, t) => sum + t.items.reduce((n, i) => n + 1 + i.children.length, 0), 0),
    [state.trades]
  );

  /* ── 커서 · 선택 · 편집 상태(리듀서 밖, 화면 지역 상태) ───────── */

  const [cursor, setCursor] = useState<Pos>(() => memory?.current?.cursor ?? START);
  const [anchor, setAnchor] = useState<Pos>(() => memory?.current?.anchor ?? START);
  useEffect(() => {
    if (memory) memory.current = { ...memory.current, cursor, anchor };
  }, [memory, cursor, anchor]);
  const [mode, setModeState] = useState<EditMode>("idle");
  // 줄 메뉴 — 잠기면 열려 있어도 닫는다(포털이라 fieldset 이 못 끈다).
  const [rowMenuOpen, setRowMenuOpen] = useState(false);
  const modeRef = useRef<EditMode>("idle");

  const curRow = resolveRow(rows, indexByKey, cursor);
  const curCol = clampCol(cursor.col, columns.length);
  const ancResolved = resolveRow(rows, indexByKey, anchor);
  const ancRow = ancResolved < 0 ? curRow : ancResolved;
  const ancCol = clampCol(anchor.col, columns.length);
  const range =
    curRow < 0
      ? null
      : normalizeRange(
          { row: ancRow, col: ancCol },
          { row: curRow, col: curCol },
          { rows: rows.length, cols: columns.length }
        );

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const composingRef = useRef(false);
  // 편집을 끝낸 뒤에도 조합이 남아 있으면 그 조합의 글자를 버린다(다음 칸에 새지 않게).
  const discardRef = useRef(false);
  const discardTrailingRef = useRef(false);
  const internalBlurRef = useRef(false);
  const dragRef = useRef<"cells" | "rows" | null>(null);
  const gateRef = useRef<ImeGate>(initImeGate());
  // 들고 있던 조합 키를 버릴 때마다 올린다 — 이미 걸어 둔 지연 실행도 무르게.
  const gateGenRef = useRef(0);
  // 되돌리기·다시 하기 직전의 행 — 바뀐 칸으로 커서를 옮기는 데 쓴다.
  const historyJumpRef = useRef<SheetRow[] | null>(null);
  const scrollWantedRef = useRef(false);
  const lastModeRef = useRef<EditMode>("idle");

  const setMode = (next: EditMode) => {
    modeRef.current = next;
    setModeState(next);
  };

  const posAt = (row: number, col: number): Pos => ({
    key: rows[row] ? rowKeyOf(rows[row]) : null,
    index: row,
    col,
  });

  const goTo = (row: number, col: number, extend = false) => {
    const next = posAt(row, col);
    setCursor((prev) => (samePos(prev, next) ? prev : next));
    if (!extend) setAnchor((prev) => (samePos(prev, next) ? prev : next));
  };

  const collapse = () => setAnchor(posAt(curRow, curCol));

  const cellAt = (r: number, c: number) => {
    const row = rows[r];
    const column = columns[c];
    // 마진 칸은 필드가 없어도(계산값) 고칠 수 있다 — 확정 때 SET_MARGIN 으로 보낸다.
    if (!isDataRow(row) || !column || (column.field === null && column.key !== "margin")) return null;
    if (!isCellEditable(row, column)) return null;
    return { row, column, field: column.field };
  };

  const ctx = { area_pyeong: state.area_pyeong, area_m2: stateAreaM2(state), spaces, vatMode };
  const focusGrid = () => inputRef.current?.focus({ preventScroll: true });

  const dropPending = () => {
    gateRef.current = clearPending(gateRef.current);
    gateGenRef.current += 1;
  };

  /* ── 입력칸 위치 · 스크롤 ──────────────────────────────────── */

  // 보기를 바꿔 다시 섰으면 떠날 때의 스크롤로 돌려놓는다 — 칸 보이기(revealCell)는 걸지 않아 돌려놓은 자리가 그대로 남는다.
  useLayoutEffect(() => {
    const saved = memory?.current;
    const container = scrollRef.current;
    if (!saved || !container) return;
    restoreSheetScroll(container, saved);
    if (saved.focus) {
      memory.current = { ...saved, focus: false };
      focusGrid();
    }
    // 처음 설 때 한 번만.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rememberScroll = () => {
    const container = scrollRef.current;
    if (!memory?.current || !container) return;
    memory.current = { ...memory.current, scrollTop: container.scrollTop, scrollLeft: container.scrollLeft };
  };

  useLayoutEffect(() => {
    const container = scrollRef.current;
    const input = inputRef.current;
    if (!container || !input) return;
    const cell = container.querySelector<HTMLElement>(`[data-cell="${curRow}:${curCol}"]`);
    if (!cell) {
      input.style.width = "0px";
      input.style.height = "0px";
      return;
    }
    const box = container.getBoundingClientRect();
    const rect = cell.getBoundingClientRect();
    input.style.top = `${rect.top - box.top + container.scrollTop - container.clientTop}px`;
    input.style.left = `${rect.left - box.left + container.scrollLeft - container.clientLeft}px`;
    input.style.width = `${rect.width}px`;
    input.style.height = `${rect.height}px`;
    // 키로 옮겼거나 편집을 열 때만, 칸이 머리글·행 번호에 가리거나 밖에 있으면 그만큼만 민다.
    const editing = mode !== "idle" && lastModeRef.current === "idle";
    lastModeRef.current = mode;
    if (!scrollWantedRef.current && !editing) return;
    scrollWantedRef.current = false;
    revealCell(container, rect, box);
  }, [curRow, curCol, rows, simple, mode]);

  useEffect(() => {
    const stop = () => {
      dragRef.current = null;
    };
    window.addEventListener("mouseup", stop);
    return () => window.removeEventListener("mouseup", stop);
  }, []);

  // 되돌리기·다시 하기 뒤 바뀐 첫 칸으로 커서를 옮긴다(엑셀처럼).
  useLayoutEffect(() => {
    const prev = historyJumpRef.current;
    if (!prev) return;
    historyJumpRef.current = null;
    const changed = firstChangedCell(prev, rows, columns);
    const r = changed ? nearestDataRow(rows, changed.row) : -1;
    if (!changed || r < 0) return;
    scrollWantedRef.current = true;
    goTo(r, changed.col ?? curCol);
    // 행이 바뀐 뒤 한 번만 본다 — 나머지는 그 렌더의 값이면 된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // 밖에서 추가한 줄의 단가 칸으로 커서를 옮긴다.
  useEffect(() => {
    if (!jumpTo) return;
    const r = indexByKey.get(`i:${jumpTo.itemKey}`);
    onJumped?.();
    if (r === undefined) return;
    const col = columns.findIndex((c) => c.key === "simple_price" || c.key === "material_unit_price");
    scrollWantedRef.current = true;
    goTo(r, col < 0 ? 0 : col);
    focusGrid();
    // seq 가 바뀔 때만 한 번 옮긴다 — 나머지는 그 렌더의 값이면 된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpTo]);

  /* ── 편집 시작 · 확정 · 취소 ───────────────────────────────── */

  // 조합이 살아 있으면 blur 로 끝내 글자를 입력칸에 확정시킨다.
  const flushComposition = () => {
    const input = inputRef.current;
    if (!input || !composingRef.current) return;
    internalBlurRef.current = true;
    input.blur();
    input.focus({ preventScroll: true });
    internalBlurRef.current = false;
  };

  const resetInput = () => {
    const input = inputRef.current;
    if (!input) return;
    if (composingRef.current) discardRef.current = true;
    input.value = "";
  };

  // 잠긴 시트는 편집 모드에 들어가지 않는다 — 재렌더 전 잠김(isLocked)도 본다.
  const beginReplace = () => {
    if (disabled || isLocked()) return;
    setMode("replace");
    collapse();
  };

  const startEdit = () => {
    if (disabled || isLocked()) return;
    const target = cellAt(curRow, curCol);
    const input = inputRef.current;
    if (!target || !input) return;
    const text = editTextOf(target.row, target.column, ctx);
    input.value = text;
    input.setSelectionRange(text.length, text.length);
    setMode("edit");
    collapse();
  };

  const cancelEdit = () => {
    dropPending();
    setMode("idle");
    resetInput();
  };

  /** 편집 중인 칸을 확정한다(then 은 같은 되돌리기 단계로). 못 읽으면 입력값·편집을 그대로 두고 false. */
  const commitEdit = (how: "key" | "click" | "blur", then?: EditorAction): boolean => {
    // 잠긴 시트는 칸 값을 보내지 않는다 — 남은 편집은 버리고 옮기기만 허락한다.
    if (disabled || isLocked()) {
      if (modeRef.current !== "idle") cancelEdit();
      return true;
    }
    const input = inputRef.current;
    if (modeRef.current === "idle" || !input) {
      runAction(then ?? null);
      return true;
    }
    if (how !== "blur") flushComposition();
    const text = input.value;
    const target = cellAt(curRow, curCol);
    const done = () => {
      setMode("idle");
      resetInput();
    };
    if (!target) {
      done();
      runAction(then ?? null);
      return true;
    }
    if (target.field === null) {
      // 고치지 않고 빠져나온 마진 칸은 보내지 않는다 — 반올림된 글자로 단가가 1원씩 움직이지 않게.
      if (text.trim() === cellText(target.row, target.column, { vatMode })) {
        done();
        runAction(then ?? null);
        return true;
      }
      const margin = commitMarginText(text);
      if (!margin.ok) {
        toast.error(margin.message, slotToast(SHEET_COMMIT_TOAST));
        return false;
      }
      const set: EditorAction = { type: "SET_MARGIN", ref: target.row.ref, margin: margin.margin };
      dispatch(then ? { type: "BATCH", actions: [set, then] } : set);
      dispatch({ type: "SEAL" });
      done();
      return true;
    }
    const result = commitCell(target.row.item, target.field, text, ctx);
    if (!result.ok) {
      toast.error(result.message, slotToast(SHEET_COMMIT_TOAST));
      return false;
    }
    const set: EditorAction = { type: "SET_ITEM", ref: target.row.ref, patch: result.patch };
    dispatch(then ? { type: "BATCH", actions: [set, then] } : set);
    dispatch({ type: "SEAL" });
    if (SUGGEST_FIELDS.has(target.field)) {
      const ref = target.row.ref;
      // 부모의 stateRef 가 방금 확정한 값을 본 뒤에 단가를 찾는다.
      window.setTimeout(() => onSuggest(ref), 0);
    }
    done();
    return true;
  };

  // 표 밖으로 나간다 — 읽히면 확정하고, 못 읽으면 값을 버리고 알린다.
  const leaveEdit = (how: "click" | "blur") => {
    if (modeRef.current === "idle") return;
    const text = inputRef.current?.value ?? "";
    if (commitEdit(how)) return;
    cancelEdit();
    toast.error(`올바르지 않은 값이라 입력을 취소했습니다 (${text})`, slotToast(SHEET_COMMIT_TOAST));
    onAbandon?.(text);
  };
  const leaveRef = useRef(leaveEdit);
  useLayoutEffect(() => {
    leaveRef.current = leaveEdit;
  });

  // 칸을 고치는 중에 잠기면 그 편집을 확정하지 않고 버린다(모드·입력값 비움).
  const cancelRef = useRef(cancelEdit);
  useLayoutEffect(() => {
    cancelRef.current = cancelEdit;
  });
  useEffect(() => {
    if (cancelsEditOnLock(disabled, modeRef.current)) cancelRef.current();
  }, [disabled]);

  // 표 밖을 누르면 편집을 끝낸다 — Safari 는 단추가 포커스를 안 가져가 blur 가 없다. pointerdown 이라 Radix 메뉴보다 먼저.
  useEffect(() => {
    const onDown = (e: Event) => {
      if (modeRef.current === "idle") return;
      const target = e.target instanceof Element ? e.target : null;
      // 공종 머리 줄은 표 안이지만 표 밖과 같다(그 줄의 Radix 메뉴는 pointerdown 을 막아 mousedown 이 안 온다).
      if (target && scrollRef.current?.contains(target) && !target.closest("[data-trade-row]")) return;
      leaveRef.current("click");
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, []);

  /* ── 범위 명령 ─────────────────────────────────────────────── */

  const runAction = (action: HistoryAction | null) => {
    // 잠긴 시트는 범위 명령(지우기·채우기·줄 메뉴)을 보내지 않는다 — 재렌더 전 잠김도 본다.
    if (!action || disabled || isLocked()) return;
    dispatch(action);
    dispatch({ type: "SEAL" });
  };

  const insertRowBelow = () => {
    scrollWantedRef.current = true;
    const row = rows[curRow];
    if (isDataRow(row)) {
      const trade = state.trades.find((t) => t.key === row.ref.tradeKey);
      if (!trade) return;
      const topKey = row.ref.parentKey ?? row.ref.itemKey;
      const index = trade.items.findIndex((i) => i.key === topKey) + 1;
      runAction({ type: "INSERT_ROWS_AT", tradeKey: trade.key, index, items: [blankFilledItem()] });
      setBothTo(insertPositionBelow(rows, curRow), curCol);
      return;
    }
    const trade = state.trades[0];
    if (!trade) return;
    runAction({
      type: "INSERT_ROWS_AT",
      tradeKey: trade.key,
      index: trade.items.length,
      items: [blankFilledItem()],
    });
    // 넣으면 공종이 펼쳐지므로 그 공종의 모든 줄(SET 자식 포함) 뒤가 새 줄 자리다.
    const header = indexByKey.get(`t:${trade.key}`) ?? 0;
    const lines = trade.items.reduce((n, i) => n + 1 + i.children.length, 0);
    setBothTo(header + 1 + lines, 0);
  };

  // 새로 만든 줄은 키를 아직 모른다 — 자리(index)로 가리킨다.
  const setBothTo = (index: number, col: number) => {
    const next: Pos = { key: null, index, col };
    setCursor(next);
    setAnchor(next);
  };

  const removeSelectedRows = () => {
    if (!range) return;
    const refs = itemRefsIn(rows, range, { children: true });
    if (refs.length === 0) return;
    runAction({ type: "REMOVE_ITEMS", refs });
    setBothTo(range.top, curCol);
  };

  const scalePrices = (ratio: number) => {
    if (!range) return;
    const refs = itemRefsIn(rows, range);
    if (refs.length === 0) return;
    runAction({ type: "SCALE_PRICES", refs, ratio });
  };

  const copyText = (): string =>
    range ? rangeToTsv(rows, columns, range, { spaces, vatMode }) : "";

  const clearRange = () => {
    if (range) runAction(clearCellsAction(rows, columns, range));
  };

  /* ── 키보드 ────────────────────────────────────────────────── */

  const undoRedo = (kind: "UNDO" | "REDO") => {
    if (disabled || isLocked() || !(kind === "UNDO" ? canUndo : canRedo)) return;
    historyJumpRef.current = rows;
    dispatch({ type: kind });
  };

  const run = (cmd: Command) => {
    // 잠기면 이동·선택·복사만 — Enter 는 줄을 늘리지 않고 아래로 옮기기만 한다(재렌더 전 잠김도 본다).
    const locked = disabled || isLocked();
    if (locked && !LOCKED_COMMANDS.has(cmd.kind)) return;
    if (cmd.kind === "undo" || cmd.kind === "redo") {
      undoRedo(cmd.kind === "undo" ? "UNDO" : "REDO");
      return;
    }
    if (curRow < 0 || !range) return;

    switch (cmd.kind) {
      case "hold":
        return;
      case "move": {
        if (commitsOnMove(locked) && !commitEdit("key")) return;
        const row = cmd.dRow === 0 ? curRow : moveRow(rows, curRow, cmd.dRow, cmd.jump);
        let col = curCol;
        if (cmd.dCol !== 0) {
          col = cmd.jump
            ? cmd.dCol > 0
              ? columns.length - 1
              : 0
            : clampCol(curCol + cmd.dCol, columns.length);
        }
        goTo(row, col, cmd.extend);
        return;
      }
      case "enter": {
        if (locked) {
          goTo(moveRow(rows, curRow, cmd.back ? -1 : 1), curCol);
          return;
        }
        const typed = modeRef.current !== "idle" && (inputRef.current?.value.trim() ?? "") !== "";
        const row = rows[curRow];
        if (!cmd.back && isDataRow(row) && isLastInTrade(rows, curRow)) {
          // 공종 마지막 줄에서 Enter 는 그 공종에 새 줄을 연다 — 값 확정과 한 되돌리기 단계. 빈 줄을 줄줄이 만들지는 않는다.
          const trade = state.trades.find((t) => t.key === row.ref.tradeKey);
          const grow = trade !== undefined && (typed || !isBlankRow(row.item));
          const insert: EditorAction | undefined =
            grow && trade
              ? {
                  type: "INSERT_ROWS_AT",
                  tradeKey: trade.key,
                  index: trade.items.length,
                  items: [blankFilledItem()],
                }
              : undefined;
          if (!commitEdit("key", insert)) return;
          if (grow) setBothTo(curRow + 1, curCol);
          return;
        }
        if (!commitEdit("key")) return;
        goTo(moveRow(rows, curRow, cmd.back ? -1 : 1), curCol);
        return;
      }
      case "tab":
        if (commitsOnMove(locked) && !commitEdit("key")) return;
        goTo(curRow, clampCol(curCol + (cmd.back ? -1 : 1), columns.length));
        return;
      case "edit":
        if (modeRef.current === "replace") setMode("edit");
        else startEdit();
        return;
      case "cancel":
        if (modeRef.current !== "idle") cancelEdit();
        else collapse();
        return;
      case "clear":
        clearRange();
        return;
      case "fillDown": {
        const fill = fillRangeOf(rows, range);
        runAction(fill ? fillDownAction(rows, columns, fill) : null);
        return;
      }
      case "selectAll": {
        const first = nearestDataRow(rows, 0);
        const last = nearestDataRow(rows, rows.length - 1);
        if (first < 0) return;
        setAnchor(posAt(last, columns.length - 1));
        setCursor(posAt(first, 0));
        return;
      }
      case "copy":
      case "cut": {
        // Safari 는 선택된 글자가 없으면 copy 이벤트를 안 낸다 — 입력칸에 TSV 를 넣고 골라 둔다.
        const input = inputRef.current;
        if (!input) return;
        const tsv = copyText();
        input.value = tsv;
        input.select();
        window.setTimeout(() => {
          if (modeRef.current === "idle" && input.value === tsv) input.value = "";
        }, 0);
        return;
      }
      default:
        return;
    }
  };

  // 조합이 끝난 뒤 늦게 실행하는 키가 그때의 커서·모드를 보게 한다.
  const runRef = useRef(run);
  useLayoutEffect(() => {
    runRef.current = run;
  });

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const native = e.nativeEvent;
    const input: KeyInput = {
      key: e.key,
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      altKey: e.altKey,
      composing: composingRef.current || native.isComposing,
    };
    scrollWantedRef.current = wantsScroll(input, modeRef.current);
    if (e.key === "Escape") dropPending();
    const is229 = native.keyCode === 229;
    const gated = gateKeyDown(gateRef.current, { ...input, is229 }, Date.now());
    gateRef.current = gated.gate;
    if (gated.decision === "swallow") {
      e.preventDefault();
      return;
    }
    if (gated.decision === "defer") {
      // 조합 확정은 브라우저에 맡기고(Enter), Tab 은 포커스만 못 빼 가게 막는다.
      if (e.key === "Tab") e.preventDefault();
      return;
    }
    const cmd = commandOf(
      { ...input, composing: gated.decision === "run" ? false : input.composing || is229 },
      modeRef.current
    );
    if (!cmd) return;
    // 복사·잘라내기는 브라우저의 copy/cut 이벤트가 이어서 와야 한다.
    if (cmd.kind !== "copy" && cmd.kind !== "cut") e.preventDefault();
    run(cmd);
  };

  /* ── 한글(IME) · 글자 입력 ─────────────────────────────────── */

  // 조합이 시작되면 그 자리에서 편집으로 올린다. 입력칸 값은 건드리지 않는다 — 첫 글자가 조합 중이다.
  const onCompositionStart = () => {
    composingRef.current = true;
    gateRef.current = startComposition(gateRef.current);
    discardRef.current = false;
    discardTrailingRef.current = false;
    if (modeRef.current !== "idle") return;
    if (cellAt(curRow, curCol) && !disabled && !isLocked()) beginReplace();
    else discardRef.current = true;
  };

  const onCompositionEnd = (e: CompositionEvent<HTMLInputElement>) => {
    composingRef.current = false;
    const ended = endComposition(gateRef.current, Date.now());
    gateRef.current = ended.gate;
    const pending = ended.run;
    if (pending) {
      // 조합 중 누른 Enter·Tab — 글자가 입력칸에 확정된 뒤 한 번만 실행한다. 그 사이 취소·blur·클릭이면 버린다.
      const gen = gateGenRef.current;
      window.setTimeout(() => {
        if (gen !== gateGenRef.current) return;
        const cmd = commandOf(pending, modeRef.current);
        if (cmd) runRef.current(cmd);
      }, 0);
    }
    if (!discardRef.current) return;
    discardRef.current = false;
    e.currentTarget.value = "";
    // Safari 는 compositionend 뒤에 input 을 한 번 더 낸다 — 같은 틱 안의 그것까지 버린다.
    discardTrailingRef.current = true;
    window.setTimeout(() => {
      discardTrailingRef.current = false;
    }, 0);
  };

  // 조합 없는 첫 글자(영문·숫자)는 input 이벤트에서 편집으로 올린다. 글자는 이미 입력칸에 있다.
  const onInput = (e: FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    if (discardTrailingRef.current || (discardRef.current && composingRef.current)) {
      discardTrailingRef.current = false;
      input.value = "";
      return;
    }
    if (modeRef.current !== "idle" || input.value === "") return;
    if (cellAt(curRow, curCol) && !disabled && !isLocked()) {
      beginReplace();
      return;
    }
    if (composingRef.current) discardRef.current = true;
    else input.value = "";
  };

  // 창을 떠나는 blur(다른 창·탭)는 편집을 그대로 두고, 화면 안에서 나가면 확정하거나 못 읽는 값을 버린다.
  const onBlur = () => {
    if (internalBlurRef.current) return;
    dropPending();
    if (modeRef.current === "idle" || !document.hasFocus()) return;
    leaveEdit("blur");
  };

  /* ── 클립보드(copy/paste 이벤트만 — 권한 API 안 씀) ──────────── */

  const onCopy = (e: ClipboardEvent<HTMLInputElement>) => {
    if (modeRef.current !== "idle" || !range) return;
    e.preventDefault();
    e.clipboardData.setData("text/plain", copyText());
    e.currentTarget.value = "";
  };

  const onCut = (e: ClipboardEvent<HTMLInputElement>) => {
    if (disabled || isLocked() || modeRef.current !== "idle" || !range) return;
    onCopy(e);
    clearRange();
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    // 잠기면(재렌더 전 포함) 선택 이동·경고 토스트 없이 붙여넣기만 삼킨다.
    if (disabled || isLocked()) {
      e.preventDefault();
      return;
    }
    if (modeRef.current !== "idle" || !range) return;
    e.preventDefault();
    const raw = e.clipboardData.getData("text/plain");
    const plan = planPaste(state, rows, columns, range, raw, { spaces });
    runAction(plan.action);
    if (plan.target) {
      // 붙인 범위만 고른다 — 새로 생긴 행은 아직 키가 없어 자리로 가리킨다(옛 행 키를 빌리면 다음 공종까지 번진다).
      const picked = pasteSelection(rows, plan.target, plan.freshFrom);
      setCursor(picked.cursor);
      setAnchor(picked.anchor);
    }
    const warning = pasteWarningOf(plan);
    if (warning) toast.warning(warning.title, { description: warning.description });
  };

  /* ── 마우스(표 전체에 한 번) ───────────────────────────────── */

  const rowIndexOf = (target: HTMLElement): number | null => {
    const tr = target.closest<HTMLElement>("tr[data-r]");
    return tr ? Number(tr.dataset.r) : null;
  };

  const onMouseDown = (e: MouseEvent<HTMLTableElement>) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    const r = rowIndexOf(target);
    const head = target.closest("[data-rowhead]");
    const td = target.closest<HTMLElement>("td[data-c]");
    // 공종 머리 줄(접기·삭제·공종명)은 표 밖과 같다 — 편집을 끝내고 그 단추에 맡긴다.
    if (target.closest("[data-trade-row]")) {
      leaveEdit("click");
      return;
    }
    if (r === null || (!head && !td)) return;
    // 포커스를 숨은 입력칸에 곧바로 붙인다 — 클릭 직후 친 키가 새지 않게 이 핸들러 안에서 동기로.
    e.preventDefault();
    focusGrid();
    scrollWantedRef.current = false;
    dropPending();
    // 못 읽는 값이 남아 있으면 칸을 옮기지 않는다(엑셀처럼).
    if (!commitEdit("click")) return;
    // 편집 없이 다른 칸을 눌러도 앞선 입력 묶음을 끊는다 — 되돌리기 한 번이 한 칸만 되돌린다.
    dispatch({ type: "SEAL" });
    if (head) {
      dragRef.current = "rows";
      setAnchor(posAt(e.shiftKey ? ancRow : r, columns.length - 1));
      setCursor(posAt(r, 0));
      return;
    }
    dragRef.current = "cells";
    goTo(r, Number(td?.dataset.c ?? 0), e.shiftKey);
  };

  const onMouseOver = (e: MouseEvent<HTMLTableElement>) => {
    if (!dragRef.current) return;
    if ((e.buttons & 1) === 0) {
      dragRef.current = null;
      return;
    }
    const target = e.target as HTMLElement;
    const r = rowIndexOf(target);
    if (r === null || !isDataRow(rows[r])) return;
    if (dragRef.current === "rows") {
      goTo(r, 0, true);
      return;
    }
    const td = target.closest<HTMLElement>("td[data-c]");
    if (td) goTo(r, Number(td.dataset.c), true);
  };

  const onDoubleClick = (e: MouseEvent<HTMLTableElement>) => {
    if ((e.target as HTMLElement).closest("td[data-c]")) startEdit();
  };

  // 도구 막대 단추는 포커스를 가져가지 않는다. 편집 중이면 먼저 확정하고, 못 읽는 값은 버린다.
  const keepFocus = (e: MouseEvent) => e.preventDefault();
  const tool = (fn: () => void) => () => {
    leaveEdit("click");
    fn();
    focusGrid();
  };

  /* ── 그리기 ────────────────────────────────────────────────── */

  const stats = useMemo(
    () => (range ? selectionStatsOf(rows, columns, range) : null),
    // range 는 매 렌더 새 객체라 네 모서리로 비교한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, columns, range?.top, range?.left, range?.bottom, range?.right]
  );
  const multi = range !== null && (range.top !== range.bottom || range.left !== range.right);
  const curColumn = columns[curCol];
  const curData = rows[curRow];
  const listId =
    curColumn?.key === "space_code"
      ? "sheet-space-list"
      : curColumn?.key === "unit"
        ? "sheet-unit-list"
        : undefined;
  const tableWidth = ROWHEAD_WIDTH + columns.reduce((sum, c) => sum + WIDTH[c.key], 0);
  const hasRange = range !== null && itemRefsIn(rows, range).length > 0;

  let no = 0;

  return (
    <SectionCard className="flex min-w-0 flex-col" padding="none">
      <fieldset className="flex min-w-0 flex-wrap items-center gap-2 border-b border-border px-3 py-2" aria-describedby={lockedDescribedBy(disabled)} data-testid="sheet-toolbar" disabled={disabled}>
        <Button disabled={disabled} size="sm" variant="outline" onClick={tool(insertRowBelow)} onMouseDown={keepFocus}>
          <Plus className="size-4" /> 줄 추가
        </Button>
        <Button
          disabled={disabled || !hasRange}
          size="sm"
          variant="outline"
          onClick={tool(removeSelectedRows)}
          onMouseDown={keepFocus}>
          <Trash2 className="size-4" /> 선택 줄 삭제
        </Button>
        <Button
          disabled={disabled || !hasRange}
          size="sm"
          variant="outline"
          onClick={tool(() => scalePrices(1.1))}
          onMouseDown={keepFocus}>
          단가 +10%
        </Button>
        <Button
          disabled={disabled || !hasRange}
          size="sm"
          variant="outline"
          onClick={tool(() => scalePrices(0.9))}
          onMouseDown={keepFocus}>
          단가 −10%
        </Button>
        {onImport && (
          <Button
            ref={importButtonRef}
            disabled={disabled}
            size="sm"
            variant="outline"
            onClick={() => {
              leaveEdit("click");
              onImport();
            }}
            onMouseDown={keepFocus}>
            <FileSpreadsheet className="size-4" /> 엑셀 가져오기
          </Button>
        )}
        <span className="mx-1 h-5 w-px bg-border" />
        <Button
          aria-label="되돌리기"
          disabled={!canUndo || disabled}
          size="sm"
          variant="ghost"
          onClick={tool(() => undoRedo("UNDO"))}
          onMouseDown={keepFocus}>
          <Undo2 className="size-4" /> 되돌리기
        </Button>
        <Button
          aria-label="다시"
          disabled={!canRedo || disabled}
          size="sm"
          variant="ghost"
          onClick={tool(() => undoRedo("REDO"))}
          onMouseDown={keepFocus}>
          <Redo2 className="size-4" /> 다시
        </Button>
        <DropdownMenu open={openUnlessLocked(rowMenuOpen, disabled)} onOpenChange={setRowMenuOpen}>
          {/* 비활성은 Trigger 에 단다 — 자식 Button 에만 달면 Radix 가 포인터다운으로 빈 메뉴를 연다. */}
          <DropdownMenuTrigger asChild disabled={!isDataRow(curData) || disabled}>
            <Button aria-label="줄 메뉴" size="sm" variant="ghost">
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              focusGrid();
            }}>
            {isDataRow(curData) && (
              <>
                <DropdownMenuItem
                  onSelect={() => runAction({ type: "MOVE_ITEM", ref: curData.ref, dir: -1 })}>
                  <ArrowUp className="size-4" /> 위로 옮기기
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => runAction({ type: "MOVE_ITEM", ref: curData.ref, dir: 1 })}>
                  <ArrowDown className="size-4" /> 아래로 옮기기
                </DropdownMenuItem>
                {curData.kind === "item" && (
                  <DropdownMenuItem
                    onSelect={() =>
                      runAction({
                        type: "ADD_CHILD",
                        tradeKey: curData.ref.tradeKey,
                        parentKey: curData.ref.itemKey,
                      })
                    }>
                    <CornerDownRight className="size-4" /> SET 하위 항목 추가
                  </DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {showCost && isDataRow(curData) && (
          <span className="ml-auto" onMouseDown={keepFocus}>
            <SaleOverrideNote
              item={curData.item}
              vatMode={vatMode}
              onReset={tool(() => runAction({ type: "RESET_TO_CALC", ref: curData.ref }))}
            />
          </span>
        )}
      </fieldset>

      <div ref={scrollRef} className="relative max-h-[max(16rem,calc(100dvh-14rem))] overflow-auto" onScroll={rememberScroll}>
        {/* 잠기면 표 안의 단추·칸을 끈다 — 숨은 캐럿은 바깥에 두어 키보드 이동은 산다. */}
        <fieldset aria-describedby={lockedDescribedBy(disabled)} className="min-w-0" data-testid="sheet-table" disabled={disabled}>
        <table
          className="w-(--table-w) table-fixed border-separate border-spacing-0"
          style={{ "--table-w": `max(100%, ${tableWidth}px)` } as CSSProperties}
          onDoubleClick={onDoubleClick}
          onMouseDown={onMouseDown}
          onMouseOver={onMouseOver}>
          <colgroup>
            <col className="w-(--col-w)" style={{ "--col-w": `${ROWHEAD_WIDTH}px` } as CSSProperties} />
            {columns.map((c) =>
              c.key === "item" ? (
                <col key={c.key} />
              ) : (
                <col key={c.key} className="w-(--col-w)" style={{ "--col-w": `${WIDTH[c.key]}px` } as CSSProperties} />
              )
            )}
          </colgroup>
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-20 h-8 border-b border-r border-border bg-muted" />
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    "sticky top-0 z-10 h-8 whitespace-nowrap border-b border-r border-border bg-muted px-1.5 text-xs font-medium text-muted-foreground",
                    c.numeric ? "text-right" : "text-left"
                  )}
                  scope="col">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <EmptyRow colSpan={columns.length + 1}>공종이 없습니다 — 아래 「공종 추가」 로 시작하세요.</EmptyRow>
            )}
            {rows.map((row, r) => {
              if (row.kind === "trade") {
                return (
                  <SheetTradeRow
                    key={rowKeyOf(row)}
                    disabled={disabled}
                    dispatch={tradeRowDispatch}
                    isLocked={isLocked}
                    leadSpan={amountCol + 1}
                    rowIndex={r}
                    tailSpan={columns.length - amountCol - 1}
                    templateItems={templateByTrade.get(row.trade.trade) ?? EMPTY_TEMPLATE}
                    trade={row.trade}
                  />
                );
              }
              no += 1;
              const inRange = multi && range !== null && r >= range.top && r <= range.bottom;
              return (
                <SheetItemRow
                  key={rowKeyOf(row)}
                  area={state.area_pyeong}
                  areaM2={stateAreaM2(state)}
                  columns={columns}
                  cursorCol={r === curRow ? curCol : -1}
                  item={row.item}
                  kind={row.kind}
                  no={no}
                  rowIndex={r}
                  selLeft={inRange && range ? range.left : -1}
                  selRight={inRange && range ? range.right : -1}
                  spaces={spaces}
                  vatMode={vatMode}
                />
              );
            })}
          </tbody>
        </table>
        </fieldset>

        {/* eslint-disable-next-line no-restricted-syntax -- raw-input: 숨은 캐럿(scripts/design-rules.mjs lineExcept) */}
        <input
          ref={inputRef}
          aria-label="칸 입력"
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          className={cn(
            "absolute left-0 top-0 m-0 h-0 w-0 rounded-none border-0 bg-transparent px-1.5 text-sm outline-hidden",
            mode === "idle"
              ? "pointer-events-none z-[5] opacity-0"
              : "z-[15] bg-background text-foreground shadow-[inset_0_0_0_2px_hsl(var(--primary))]",
            curColumn?.numeric && "text-right tabular-nums"
          )}
          list={listId}
          readOnly={disabled}
          spellCheck={false}
          onBlur={onBlur}
          onCompositionEnd={onCompositionEnd}
          onCompositionStart={onCompositionStart}
          onCopy={onCopy}
          onCut={onCut}
          onInput={onInput}
          onKeyDown={onKeyDown}
          onMouseDown={() => {
            if (modeRef.current === "replace") setMode("edit");
          }}
          onPaste={onPaste}
        />
        <datalist id="sheet-space-list">
          {spaces.map((s) => (
            <option key={s.code} value={s.label} />
          ))}
        </datalist>
        <datalist id="sheet-unit-list">
          {UNITS.map((u) => (
            <option key={u} value={u} />
          ))}
        </datalist>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border px-3 py-1.5 text-xs text-muted-foreground">
        {stats && (
          <span className="tabular-nums">
            {stats.rows}줄 × {stats.cols}칸
          </span>
        )}
        {stats && stats.sum !== null && (
          <span className="tabular-nums">합계 {formatSum(stats.sum)}</span>
        )}
        <span className="tabular-nums">항목 {itemCount}개</span>
        <span className="ml-auto hidden lg:inline">
          Enter 아래 · Tab 오른쪽 · F2 편집 · ⌘D 채우기 · ⌘Z 되돌리기
        </span>
      </div>
    </SectionCard>
  );
};

export default SheetView;
